import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { pitchType, ToggleButtons } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import StrikeZone from './StrikeZone';

const plotPitches = (translate, pitches) => {
    const series = [
        { data: [], name: translate('components.non-dirt-blocking.got-away'), index: 1, id: 'gotaway', color: '#bf0b23', marker: { radius: 4 } },
        { data: [], name: translate('components.non-dirt-blocking.caught'), index: 2, id: 'caught', color: '#777777', marker: { radius: 2 } },
    ];

    pitches.forEach((pitch) => {
        if (pitch.platex) {
            const tooltip = [
                `${translate('components.blocking-chart.wppb-probability', { value: (pitch.pwppbalt * 100).toFixed(1) })}`,
                `${translate('components.non-dirt-blocking.called')} ${(pitch.isstrike ? translate('baseball.strikes_other') : translate('baseball.balls_one'))}`,
                `${translate('components.non-dirt-blocking.pitch-type')}: ${pitchType(pitch.pitchtype, true, undefined, translate)}`,
                `${(pitch.islhb ? `${translate('baseball.lhh')}  - ` : `${translate('baseball.rhh')} - `)} ${pitch.battername}`,
                `${(pitch.islhp ? `${translate('baseball.lhp')} - ` : `${translate('baseball.rhp')} - `)} ${pitch.pitchername}`,
            ].join('<br/>');
            const point = {
                x: pitch.platex * -1,
                y: pitch.platez,
                name: pitch.getaway ? translate('components.non-dirt-blocking.got-away') : translate('components.non-dirt-blocking.caught'),
                tooltip,
                url: pitch.url,
                gameid: pitch.gameid,
                eventseq: pitch.eventseq,
                pitchseq: pitch.pitchseq,
                id: `${pitch.gameid}_${pitch.eventseq}_${pitch.pitchseq}`,
            };

            if (pitch.getaway === 1) series[0].data.push(point);
            else series[1].data.push(point);
        }
    });

    return series;
};

const getPlotBoundaries = (seriesData) => {
    let minX = -2.25;
    let maxX = 2.25;
    let minY = 0.25;
    let maxY = 4.75;
    const xValues = seriesData.map((d) => d.platex).sort((a, b) => Number(a) - Number(b));
    const yValues = seriesData.map((d) => d.platez).sort((a, b) => Number(a) - Number(b));

    let xOffset = 0;
    let yOffset = 0;
    if (xValues.length) {
        let minXOffset = 0;
        let maxXOffset = 0;
        if (xValues[0] < -2.5) {
            minXOffset = xValues[0] - minX;
        }
        if (xValues[xValues.length - 1] > 2.25) {
            maxXOffset = xValues[xValues.length - 1] - maxX;
        }
        xOffset = minXOffset > maxXOffset ? minXOffset : maxXOffset;
    }
    if (yValues.length) {
        let minYOffset = 0;
        let maxYOffset = 0;
        if (yValues[0] < 0.25) {
            minYOffset = yValues[0] - minY;
        }
        if (yValues[yValues.length - 1] > 4.75) {
            maxYOffset = yValues[yValues.length - 1] - maxY;
        }
        yOffset = minYOffset > maxYOffset ? minYOffset : maxYOffset;
    }

    minX -= xOffset;
    maxX += xOffset;
    minY -= yOffset;
    maxY += yOffset;
    return { minX, maxX, minY, maxY };
};

const VIEW = { ALL: 'all', LHH: 'lhh', RHH: 'rhh' };

const NonDirtBlockingChart = ({ pitches, contoursLhb, contoursRhb, onClick }) => {
    const { t: translate } = useTranslation();
    const [view, setView] = useState(VIEW.ALL);
    const options = [
        { name: translate('common.all'), value: VIEW.ALL, variant: 'default' },
        { name: translate('baseball.lhh'), value: VIEW.LHH, variant: 'default' },
        { name: translate('baseball.rhh'), value: VIEW.RHH, variant: 'default' },
    ];

    const { islhp } = pitches.length > 0 ? pitches[0] : false;
    let contours = [];
    if (view === VIEW.LHH) {
        contours = islhp ? contoursLhb.lhp : contoursLhb.rhp;
    } else if (view === VIEW.RHH) {
        contours = islhp ? contoursRhb.lhp : contoursRhb.rhp;
    }

    const filteredPitchlog = pitches.filter((pitch) => (view === VIEW.LHH && pitch.islhb) || (view === VIEW.RHH && !pitch.islhb) || view === VIEW.ALL);
    const seriesData = plotPitches(translate, filteredPitchlog);
    const { minX, maxX, minY, maxY } = getPlotBoundaries(filteredPitchlog);

    return (
        <>
            <ToggleButtons name="nonblocking" size="sm" options={options} onChange={(value) => setView(value)} defaultValue={VIEW.ALL} />
            <StrikeZone enableZoom={false} showlegend minX={minX} maxX={maxX} minY={minY} maxY={maxY} seriesData={seriesData} contourPoints={contours} showbatter={view !== VIEW.ALL} showlhb={view === VIEW.LHH} onClick={(p) => onClick(p)} />
        </>
    );
};

NonDirtBlockingChart.propTypes = {
    pitches: PropTypes.arrayOf(PropTypes.object).isRequired,
    contoursLhb: PropTypes.shape({
        rhp: PropTypes.arrayOf(PropTypes.object),
        lhp: PropTypes.arrayOf(PropTypes.object),
    }),
    contoursRhb: PropTypes.shape({
        rhp: PropTypes.arrayOf(PropTypes.object),
        lhp: PropTypes.arrayOf(PropTypes.object),
    }),
    onClick: PropTypes.func,
};

NonDirtBlockingChart.defaultProps = {
    contoursLhb: {},
    contoursRhb: {},
    onClick: undefined,
};

export default NonDirtBlockingChart;
